import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Track } from '@app/core/models/Track';
import { AppState } from '@app/core/states/app.state';
import { LibraryModalService } from '@app/library/services/library-modal.service';
import {
  TrackCoverDeleteFailure,
  TrackCoverDeleteRequest,
  TrackCoverDeleteSuccess,
  TrackCoverLookupFailure,
  TrackCoverLookupRequest,
  TrackCoverLookupSuccess,
  TrackCoverMultipleSetFailure,
  TrackCoverMultipleSetRequest,
  TrackCoverMultipleSetSuccess,
  TrackCoverSingleSetFailure,
  TrackCoverSingleSetRequest,
  TrackCoverSingleSetSuccess,
} from '@app/library/states/tracks.actions';
import { TracksState } from '@app/library/states/tracks.state';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { UiStoreHelperService } from '@radioking/shared/common-services';
import { Observable, of, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';

interface ErrorData {
  [name: string]: boolean;
}

const placeholder = 'assets/placeholder-square.png';

@Component({
  selector: 'rk-track-image-upload',
  templateUrl: './track-image-upload.component.html',
  styleUrls: ['./track-image-upload.component.scss'],
})
export class TrackImageUploadComponent implements OnInit, OnDestroy {
  accept = 'image/jpeg,image/png';

  isMultiple = false;

  errors: ErrorData = {};

  // default to 3 mb
  @Input() maxFileSize = 3145748;

  @Input()
  minHeight = 400;

  @Input()
  minWidth = 400;

  @Select(AppState.onMobile)
  onMobile$: Observable<boolean>;

  get tracks(): Track[] {
    return this._tracks;
  }

  @Input() set tracks(value: Track[]) {
    if (!value || value.length === 0) {
      return;
    }
    this.isMultiple = value.length > 1;
    this._tracks = value;
    if (this.isMultiple) {
      this.imgUrl$ = of(placeholder);
    } else {
      this.imgUrl$ = this.store.select(TracksState.trackCoverWithId).pipe(
        map(fn => fn(this.tracks[0].id)),
        map(val => (val ? val : placeholder)),
      );
    }
  }

  private _tracks: Track[];

  imgUrl$: Observable<string>;

  loading$: Observable<boolean>;

  @ViewChild('basicfileinput', { static: true })
  basicFileInput: ElementRef;

  duplicateIEEvent: boolean; // flag to recognize duplicate onchange event for file input

  subscription = new Subscription();

  constructor(
    private readonly store: Store,
    private readonly uiHelper: UiStoreHelperService,
    private readonly action$: Actions,
    private readonly modalService: LibraryModalService,
  ) {}

  ngOnInit() {
    this.subscription.add(
      this.action$
        .pipe(ofActionDispatched(TrackCoverMultipleSetSuccess))
        .subscribe(data => {
          this.imgUrl$ = of(data.tracksCover[0].cover_url);
        }),
    );
    this.loading$ = this.uiHelper.getLoadingState(
      [
        TrackCoverMultipleSetRequest,
        TrackCoverSingleSetRequest,
        TrackCoverLookupRequest,
        TrackCoverDeleteRequest,
      ],
      [
        TrackCoverMultipleSetFailure,
        TrackCoverMultipleSetSuccess,
        TrackCoverSingleSetFailure,
        TrackCoverSingleSetSuccess,
        TrackCoverLookupFailure,
        TrackCoverLookupSuccess,
        TrackCoverDeleteFailure,
        TrackCoverDeleteSuccess,
      ],
    );

    this.subscription.add(
      this.action$.pipe(ofActionDispatched(TrackCoverDeleteSuccess)).subscribe(() => {
        if (this.tracks.length === 1) {
          this.tracks[0].isCustomCover = false;
        }
      }),
    );

    this.subscription.add(
      this.action$.pipe(ofActionDispatched(TrackCoverSingleSetSuccess)).subscribe(() => {
        if (this.tracks.length === 1) {
          this.tracks[0].isCustomCover = true;
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  async onFileSelect(event: any) {
    if (event.type !== 'drop' && this.isIE11() && this.duplicateIEEvent) {
      this.duplicateIEEvent = false;

      return;
    }

    this.errors = {};

    const files = event.dataTransfer ? event.dataTransfer.files : event.target.files;
    const file = files[0];
    if (!file) {
      return;
    }

    if (await this.validate(file)) {
      if (this.isImage(file)) {
        if (this.isMultiple) {
          this.modalService.editMultipleTrack(null, null, this.tracks, file);
          // this.store.dispatch(new TrackCoverMultipleSetRequest(this.tracks, file));
        } else {
          this.store.dispatch(new TrackCoverSingleSetRequest(this.tracks[0], file));
        }
      }
    }
  }

  onLookupFile() {
    this.store.dispatch(new TrackCoverLookupRequest(this.tracks[0]));
  }

  onDeleteFile() {
    this.imgUrl$.pipe(take(1)).subscribe(val => {
      if (val === placeholder) {
        return;
      }
      this.store.dispatch(new TrackCoverDeleteRequest(this.tracks[0]));
    });
    if (this.basicFileInput) {
      this.basicFileInput.nativeElement.value = '';
    }
  }

  onClickAddFiles() {
    if (this.basicFileInput) {
      this.basicFileInput.nativeElement.click();
    }
  }

  async validate(file: File): Promise<boolean> {
    if (this.accept && !this.isFileTypeValid(file)) {
      this.errors = {
        invalidFileType: true,
      };

      return false;
    }

    if (this.maxFileSize && file.size > this.maxFileSize) {
      this.errors = {
        maxFileSize: true,
      };

      return false;
    }

    if (!(await this.checkImageSize(file))) {
      this.errors = {
        fileToSmall: true,
      };

      return false;
    }

    return true;
  }

  private isFileTypeValid(file: File): boolean {
    const acceptableTypes = this.accept.split(',');
    for (const type of acceptableTypes) {
      const acceptable = this.isWildcard(type)
        ? this.getTypeClass(file.type) === this.getTypeClass(type)
        : file.type === type ||
          this.getFileExtension(file).toLowerCase() === type.toLowerCase();

      if (acceptable) {
        return true;
      }
    }

    return false;
  }

  private async checkImageSize(file: File): Promise<boolean> {
    const img = await this.loadImage(file);

    return img.width >= this.minWidth && img.height >= this.minHeight;
  }

  private async loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>(resolve => {
      const fr = new FileReader();

      fr.onload = () => {
        const img = new Image();

        img.onload = () => {
          resolve(img);
        };
        img.src = fr.result as string;
      };

      fr.readAsDataURL(file);
    });
  }

  isIE11() {
    return !!window['MSInputMethodContext'] && !!document['documentMode'];
  }

  isWildcard(fileType: string): boolean {
    return fileType.indexOf('*') !== -1;
  }

  getFileExtension(file: File): string {
    return `.${file.name.split('.').pop()}`;
  }

  getTypeClass(fileType: string): string {
    return fileType.substring(0, fileType.indexOf('/'));
  }

  isImage(file: File): boolean {
    return /^image\//.test(file.type);
  }

  hasCustomCover(): boolean {
    return this._tracks.length === 1 && this._tracks[0].isCustomCover;
  }

  maxFileMb(): number {
    return Math.floor(this.maxFileSize / 1048576);
  }
}
